/**
 * Coordinator profile — chunk 3, item 12.
 *
 * CoordProfilePage issued four .from() reads carrying FOUR relation embeds, all
 * of which the proxy silently dropped, plus one read that failed outright.
 * Contract mirror: labourcore-api/src/modules/coords/coords.types.ts.
 *
 * ── What starts working ───────────────────────────────────────────────────
 *
 * 1. The engagement list rendered `p.operative?.full_name || "Operative"` on
 *    every row. Worse, three fields were `&&`-guarded on undefined values, so
 *    they did not fall back to a placeholder — they were simply absent:
 *      • the operative's trade
 *      • THE PARTNER COMPANY'S NAME — a coordinator's history has never shown
 *        which partner supplied the crew
 *      • the requirement's location
 *
 * 2. Disputes: `{d.partner_co?.name && …}` — no dispute has ever shown who
 *    filed it.
 *
 * 3. The disputes read filtered `.is("deleted_at", null)` on a column that does
 *    not exist on `disputes`. The proxy validates is_filter columns and returns
 *    400; the shim yields `{ data: null, error }` rather than throwing, so the
 *    section fell back to an empty list. THE DISPUTES SECTION HAS ALWAYS BEEN
 *    EMPTY for every coordinator.
 *
 * 4. Invoices asked for `nullsFirst: false`, which the shim drops, so unsent
 *    invoices sorted above sent ones. Third instance of that defect.
 */

import { apiGet } from "./client";
import type { Row } from "./findLabour";

export interface CompanyRef {
  id: string | null;
  name: string | null;
}

export interface CoordProfile {
  company: Row;
  placements: Array<Row & {
    operative: { full_name: string | null; trade: string | null } | null;
    requirement: {
      trade: string | null;
      headcount: number | null;
      start_date: string | null;
      location: string | null;
    } | null;
    partner_co: CompanyRef | null;
  }>;
  invoices: Row[];
  disputes: Array<Row & { partner_co: CompanyRef | null }>;
}

/**
 * One coordinator's profile, in one request.
 *
 * Throws ApiError(404,'not_found') when the company does not exist.
 */
export function fetchCoordProfile(coordId: string): Promise<CoordProfile> {
  return apiGet<CoordProfile>(`/api/coords/${coordId}/profile`);
}

/* ────────────────────────────────────────────────────────────────────────────
 * The rest of chunk 3. Each of these replaced a component that composed its own
 * queries; the defects are recorded in Live-Defects-Log.md.
 */

import { apiPost, apiPut, apiDelete } from "./client";

export interface PartnerNetwork {
  companies: Row[];
  placements: Row[];
}

/** Company + placement edges for the network graph. */
export function fetchPartnerNetwork(): Promise<PartnerNetwork> {
  return apiGet<PartnerNetwork>("/api/partners/network");
}

export interface PartnerRelated {
  active_projects: Row[];
  open_requirement_trades: Array<{ project_id: number | null; trade: string | null }>;
  /**
   * Projects this partner is already placed on — the match scroller's exclusion
   * set. It was derived from a select consisting of nothing but an embed, which
   * the proxy dropped, so it has always been empty and the scroller has been
   * recommending projects the partner already works on.
   */
  placed_project_ids: Array<number | null>;
  compliance_documents: Row[];
  latest_placement:
    | (Row & {
        requirement: { trade: string | null; location: string | null } | null;
        operative: { full_name: string | null } | null;
      })
    | null;
}

/** Project matches, compliance documents and the latest shared placement. */
export function fetchPartnerRelated(partnerId: string): Promise<PartnerRelated> {
  return apiGet<PartnerRelated>(`/api/partners/${partnerId}/related`);
}

/* ── Match weights ──────────────────────────────────────────────────────── */

export function fetchMatchWeights(): Promise<{ weights: Record<string, number> | null }> {
  return apiGet("/api/match-weights");
}
export function saveMatchWeights(weights: Record<string, number>): Promise<{ ok: true }> {
  return apiPut("/api/match-weights", { weights });
}
export function resetMatchWeights(): Promise<{ ok: true }> {
  return apiDelete("/api/match-weights");
}

/* ── Public score share tokens ──────────────────────────────────────────── */

export interface ScoreToken extends Row {
  id: string;
  company_id: string;
  token: string;
  label: string | null;
  revoked_at: string | null;
}

/**
 * The company is validated against the caller's memberships server-side. The
 * screen used to pass it with nothing checking it, on a table with RLS
 * disabled — and `public_score_tokens` is one of the nine sensitive tables
 * still readable by any authenticated client. Once this screen stops reading it
 * directly the table can leave the proxy allowlist.
 */
export function fetchScoreTokens(companyId: string): Promise<ScoreToken[]> {
  return apiGet<ScoreToken[]>("/api/score-tokens", { companyId });
}
/** The token value is generated by a database default, never by the caller. */
export function createScoreToken(companyId: string, label: string | null): Promise<ScoreToken> {
  return apiPost<ScoreToken>("/api/score-tokens", { companyId, label });
}
export function revokeScoreToken(tokenId: string): Promise<{ ok: true }> {
  return apiPost(`/api/score-tokens/${tokenId}/revoke`);
}

/** Coordinator invites issued for one origination. */
export function fetchOriginationInvites(originationId: string): Promise<Row[]> {
  return apiGet<Row[]>(`/api/originations/${originationId}/invites`);
}
